import { useCallback, useEffect, useState } from "react";
import { CallToolResult, GetPromptResult, ReadResourceResult } from "@modelcontextprotocol/sdk/types.js";

import { mcpClientService, MCPClientState } from "mcp/services/MCPClientService";
import { MCPServerConfig } from "../services/MCPConfigService";

export interface UseMCPResult extends MCPClientState {
    callTool: (name: string, args?: Record<string, unknown>) => Promise<CallToolResult>;
    clearError: () => void;
    connect: (config: MCPServerConfig) => Promise<void>;
    disconnect: () => Promise<void>;
    forceReconnect: () => Promise<void>;
    getPrompt: (name: string, args?: Record<string, unknown>) => Promise<GetPromptResult>;
    readResource: (uri: string) => Promise<ReadResourceResult>;
}

export function useMCP(): UseMCPResult {
    const [state, setState] = useState<MCPClientState>(mcpClientService.getState());

    useEffect(() => {
        return mcpClientService.subscribe(setState);
    }, []);

    const connect = useCallback(async (config: MCPServerConfig) => {
        if (!mcpClientService.isEnabled()) {
            console.log("[useMCP] Connect called but Cindra is disabled");
            return; // Gracefully handle disabled state
        }
        await mcpClientService.connect(config);
    }, []);

    const disconnect = useCallback(async () => {
        if (!mcpClientService.isEnabled()) {
            console.log("[useMCP] Disconnect called but Cindra is disabled");
            return; // Gracefully handle disabled state
        }
        await mcpClientService.disconnect();
    }, []);

    const forceReconnect = useCallback(async () => {
        if (!mcpClientService.isEnabled()) {
            console.log("[useMCP] Force reconnect called but Cindra is disabled");
            return; // Gracefully handle disabled state
        }
        await mcpClientService.forceReconnect();
    }, []);

    const callTool = useCallback(async (name: string, args?: Record<string, unknown>) => {
        if (!mcpClientService.isEnabled()) {
            throw new Error("Cindra AI features are not available. Contact your administrator to enable them.");
        }
        return await mcpClientService.callTool(name, args);
    }, []);

    const readResource = useCallback(async (uri: string) => {
        if (!mcpClientService.isEnabled()) {
            throw new Error("Cindra AI features are not available. Contact your administrator to enable them.");
        }
        return await mcpClientService.readResource(uri);
    }, []);

    const getPrompt = useCallback(async (name: string, args?: Record<string, unknown>) => {
        if (!mcpClientService.isEnabled()) {
            throw new Error("Cindra AI features are not available. Contact your administrator to enable them.");
        }
        return await mcpClientService.getPrompt(name, args);
    }, []);

    const clearError = useCallback(() => {
        if (mcpClientService.isEnabled()) {
            mcpClientService["updateState"]({ error: null });
        }
    }, []);

    return {
        ...state,
        callTool,
        clearError,
        connect,
        disconnect,
        forceReconnect,
        getPrompt,
        readResource
    };
}
